import {i18n as _i18n, i18nInitialized, i18nReady} from '../i18n';

/**
 * Locales wrapper to ensure that values are set when i18n has been initialized
 * @param cb Callback function that returns the localized strings
 * @returns Localized strings
 *
 * @example
 * export const SchemaValidationErrorLabel = Locales((i18n) => ({
 * 	DATE_INVALID: i18n('i18n.invalidDate'),
 * 	REQUIRED: i18n('i18n.required'),
 * }));
 */
export const Locales = <
	T extends Record<string | number, string> = Record<string | number, string>,
	F extends (...args: any[]) => any = (i18n: typeof _i18n.t) => T,
>(
	cb: F,
): ReturnType<F> => {
	let currentLanguage = _i18n.language;

	const listen = () => {
		_i18n.on('languageChanged', (lng) => {
			if (currentLanguage === lng) return;

			if (!_i18n.hasResourceBundle(lng, 'translation')) {
				const handleLoaded = () => {
					Object.assign(results, cb(_i18n.t.bind(_i18n)));
					_i18n.off('loaded', handleLoaded);
				};
				_i18n.on('loaded', handleLoaded);
			} else {
				Object.assign(results, cb(_i18n.t.bind(_i18n)));
			}
			currentLanguage = _i18n.language;
		});
	};

	// we will just abuse the object reference
	// we are still going to need to make a react hook for locales
	// so that one day we don't have to reload the entire site when that changes
	// just update the app context and any components rendered will re-render with
	// the updated locales
	// in any event, still think it may be nice to keep things wrapped in this so
	// that we can deal with the lib directly here and not a thousand places
	const results = {} as ReturnType<F>;
	if (!i18nReady) {
		Object.assign(results, cb(_i18n.t.bind(_i18n)));
		i18nInitialized.then(() => {
			Object.assign(results, cb(_i18n.t.bind(_i18n)));
			listen();
		});
	} else {
		Object.assign(results, cb(_i18n.t.bind(_i18n)));
		listen();
	}

	return results;
};

export default Locales;
